import api from './client';
import { _filenameFromResponse } from './results';
import { downloadBlob } from '../utils/download';

export interface DbStats {
  users: number;
  tests: number;
  questions: number;
  answer_options: number;
  results: number;
  conclusions: number;
  payments: number;
  custom_answer_reviews: number;
  pdfs: number;
}

export interface DbImportReport {
  [table: string]: {
    added: number;
    skipped?: number;
    errors?: number;
  };
}

export interface DbImportResponse {
  status: string;
  report: DbImportReport;
}

export interface ScenarioBundleImportResponse {
  status: string;
  skipped: boolean;
  test_id: number;
  media_added: number;
  media_skipped: number;
  media_missing: string[];
  questions_added: number;
  options_added: number;
}

export const getDbStats = async (): Promise<DbStats> => {
  const response = await api.get('/db/stats');
  return response.data;
};

export interface DbExportResult {
  size: number;
  includePdfs: boolean;
}

export const exportDb = async (
  includePdfs = false,
  onProgress?: (loaded: number, total: number) => void,
): Promise<DbExportResult> => {
  const response = await api.get('/db/export', {
    params: { include_pdfs: includePdfs },
    responseType: 'blob',
    onDownloadProgress: (e) => {
      if (e.total && onProgress) onProgress(e.loaded, e.total);
    },
  });
  const filename = _filenameFromResponse(response, 'psydecrypt-db-export.json');
  const size = (response.data as Blob).size;
  downloadBlob(response.data as Blob, filename);
  return { size, includePdfs };
};

export const importDb = async (
  file: File,
  onProgress?: (loaded: number, total: number) => void,
): Promise<DbImportResponse> => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await api.post('/db/import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: (e) => {
      if (e.total && onProgress) onProgress(e.loaded, e.total);
    },
  });
  return response.data;
};

/** Экспорт zip-бандла сценария теста вместе с его графикой. */
export const exportScenarioBundle = async (
  testId: number,
  testTitle: string,
  onProgress?: (loaded: number, total: number) => void,
): Promise<number> => {
  const response = await api.get(`/db/scenario/${testId}/export`, {
    responseType: 'blob',
    onDownloadProgress: (e) => {
      if (e.total && onProgress) onProgress(e.loaded, e.total);
    },
  });
  const blob: Blob = response.data instanceof Blob
    ? response.data
    : new Blob([response.data], { type: 'application/zip' });
  downloadBlob(blob, `scenario_${testId}_${testTitle.replace(/[^a-zа-яё0-9]/gi, '_')}.zip`);
  return blob.size;
};

/** Импорт zip-бандла сценария с графикой (восстанавливает медиа и тест). */
export const importScenarioBundle = async (
  file: File,
  onProgress?: (loaded: number, total: number) => void,
): Promise<ScenarioBundleImportResponse> => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await api.post('/db/scenario/import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    onUploadProgress: (e) => {
      if (e.total && onProgress) onProgress(e.loaded, e.total);
    },
  });
  return response.data;
};